import { EntityWithConsumableDescendants } from '@agilelab/plugin-wb-marketplace-common';
import { PolicyViolationsCountMap } from '@agilelab/plugin-wb-governance-common';

/**
 * Builds a lookup map of external IDs for each input entity, collecting the external IDs of its direct components
 * and one level of nested sub-components.
 *
 * @param items - Array of entities (EntityWithConsumableDescendants[]) expected to have an `externalId` and optional `components`.
 * @returns A Record mapping each entity's `externalId` to an array of unique descendant external IDs (strings).
 *
 * @example
 * // Given an entity with a component B that has a sub-component C:
 * // [{ externalId: 'A', components: [{ externalId: 'B', components: [{ externalId: 'C' }] }] }]
 * // returns: { A: ['B', 'C'] }
 */
export const getAllExternalIdsMap = (
  items: EntityWithConsumableDescendants[],
): Record<string, string[]> => {
  const result: Record<string, string[]> = {};

  for (const item of items) {
    const descendantIds =
      item.components?.flatMap(comp => {
        const subIds = comp.components?.map(sub => sub.externalId) ?? [];
        return [comp.externalId, ...subIds];
      }) ?? [];

    const uniqueIds = Array.from(
      new Set(descendantIds.filter((id): id is string => Boolean(id))),
    );

    result[item.externalId] = uniqueIds;
  }

  return result;
};

// add doc
/**
 * Extracts and returns unique, truthy external IDs from a collection of entities and their nested components.
 *
 *
 * @param items - Array of entities that may contain:
 *   - externalId?: string
 *   - components?: Array<{
 *       externalId?: string,
 *       components?: Array<{ externalId?: string }>
 *     }>
 *
 * @returns An array of unique, truthy external IDs (strings) found among the top-level entities,
 * their direct components, and their nested subcomponents.
 *
 * @example
 * // Given:
 * // [
 * //   { externalId: 'a', components: [{ externalId: 'b', components: [{ externalId: 'c' }] }] },
 * //   { externalId: 'b' } // duplicate 'b'
 * // ]
 * // The function returns: ['a', 'b', 'c']
 */
export const extractIds = (
  items: EntityWithConsumableDescendants[],
): string[] => {
  const descendantIds = items.flatMap(item => {
    const compIds =
      item.components?.flatMap(comp => {
        const subIds = comp.components?.map(sub => sub.externalId) ?? [];
        return [comp.externalId, ...subIds];
      }) ?? [];
    return [item.externalId, ...compIds];
  });

  return Array.from(
    new Set(descendantIds.filter((id): id is string => Boolean(id))),
  );
};

export const getAggregatedPolicyViolationsCountMap = (
  individualViolations: PolicyViolationsCountMap,
): Record<'info' | 'warning' | 'error', number> => {
  const aggregatedViolations = new Map<string, number>();

  for (const violations of individualViolations.values()) {
    for (const [outcome, count] of violations.entries()) {
      let normalizedOutcome: 'info' | 'warning' | 'error' | null = null;

      switch (outcome) {
        case 'info':
          normalizedOutcome = 'info';
          break;
        case 'warning':
        case 'not_blocking_error':
          normalizedOutcome = 'warning';
          break;
        case 'error':
          normalizedOutcome = 'error';
          break;
        default:
          break;
      }

      if (normalizedOutcome) {
        aggregatedViolations.set(
          normalizedOutcome,
          (aggregatedViolations.get(normalizedOutcome) ?? 0) + count,
        );
      }
    }
  }

  return {
    info: aggregatedViolations.get('info') ?? 0,
    warning: aggregatedViolations.get('warning') ?? 0,
    error: aggregatedViolations.get('error') ?? 0,
  };
};
